import {latLng} from 'leaflet';

export default class Geography {
}

// Constants

Geography.EARTH_RADIUS_MEAN = 6371000;
Geography.EARTH_RADIUS_WGS84 = 6378137;
Geography.PIover180 = 0.0174532925; // Math.PI / 180

// Static functions

Geography.toRad = function(n) {
	return n * Geography.PIover180;
}

Geography.toDeg = function(n) {
	return n * 180 / Math.PI;
}

Geography.destVincenty = function(lat1, lon1, brng, dist) {
	var a = 6378137,
	     b = 6356752.3142,
	     f = 0.003352811, // WGS-84 ellipsoid
	     s = dist,
	     alpha1 = Geography.toRad(brng),
	     sinAlpha1 = Math.sin(alpha1),
	     cosAlpha1 = Math.cos(alpha1),
	     tanU1 = (1 - f) * Math.tan(Geography.toRad(lat1)),
	     cosU1 = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1,
	     sigma1 = Math.atan2(tanU1, cosAlpha1),
	     sinAlpha = cosU1 * sinAlpha1,
	     cosSqAlpha = 1 - sinAlpha * sinAlpha,
	     uSq = cosSqAlpha * (a * a - b * b) / (b * b),
	     A = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq))),
	     B = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq))),
	     sigma = s / (b * A),
	     sigmaP = 2 * Math.PI;
	
	var cos2SigmaM, sinSigma, cosSigma, deltaSigma;
	
	while (Math.abs(sigma - sigmaP) > 1e-12) {
		cos2SigmaM = Math.cos(2 * sigma1 + sigma);
		sinSigma = Math.sin(sigma);
		cosSigma = Math.cos(sigma);
		deltaSigma = B * sinSigma * (cos2SigmaM + B / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) - B / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
		sigmaP = sigma;
		sigma = s / (b * A) + deltaSigma;
	}
	
	var tmp = sinU1 * sinSigma - cosU1 * cosSigma * cosAlpha1,
	     lat2 = Math.atan2(sinU1 * cosSigma + cosU1 * sinSigma * cosAlpha1, (1 - f) * Math.sqrt(sinAlpha * sinAlpha + tmp * tmp)),
	     lambda = Math.atan2(sinSigma * sinAlpha1, cosU1 * cosSigma - sinU1 * sinSigma * cosAlpha1),
	     C = f / 16 * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha)),
	     dl = lambda - (1 - C) * f * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM))),
	     revAz = Math.atan2(sinAlpha, -tmp); // final bearing
	
	return latLng(Geography.toDeg(lat2), lon1 + Geography.toDeg(dl));
}

Geography.destSpherical = function(lat1, lon1, brng, dist) {
	// sinφ2 = sinφ1⋅cosδ + cosφ1⋅sinδ⋅cosθ
	// tanΔλ = sinθ⋅sinδ⋅cosφ1 / cosδ−sinφ1⋅sinφ2
	// see mathforum.org/library/drmath/view/52049.html for derivation

	var δ = Number(dist) / WeatherNet.Geography.EARTH_RADIUS_MEAN; // angular distance in radians
	var θ = Geography.toRad(Number(brng));

	var φ1 = Geography.toRad(lat1);
	var λ1 = Geography.toRad(lon1);

	var sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1);
	var sinδ = Math.sin(δ), cosδ = Math.cos(δ);
	var sinθ = Math.sin(θ), cosθ = Math.cos(θ);

	var sinφ2 = sinφ1*cosδ + cosφ1*sinδ*cosθ;
	var φ2 = Math.asin(sinφ2);
	var y = sinθ * sinδ * cosφ1;
	var x = cosδ - sinφ1 * sinφ2;
	var λ2 = λ1 + Math.atan2(y, x);

	return latLng(Geography.toDeg(φ2), (Geography.toDeg(λ2)+540)%360-180); // normalise to −180..+180°
};

Geography.destMeters = function(x, y, brng, dist) {
	var θ = Geography.toRad(Number(brng));
	var x2 = x + dist * Math.sin(θ);
	var y2 = y + dist * Math.cos(θ);
	return {"x": x2, "y": y2};
}

Object.freeze(Geography);
